import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Building2 } from "lucide-react";

export default function Login() {
  const { login, error } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    const ok = await login(email, password);
    setLoading(false);
    if (ok) navigate("/dashboard");
  };

  const fill = (em, pw) => { void em; void pw; };

  return (
    <div className="min-h-screen grid grid-cols-1 lg:grid-cols-2">
      {/* Left visual */}
      <div
        className="hidden lg:block relative noise-overlay"
        style={{
          backgroundImage:
            "url(https://images.unsplash.com/photo-1619218070141-bcfeb8b93074?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2MDV8MHwxfHNlYXJjaHwxfHxtb2Rlcm4lMjBob3N0ZWwlMjBidWlsZGluZyUyMGV4dGVyaW9yJTIwYXJjaGl0ZWN0dXJlfGVufDB8fHx8MTc4NTgyNzk5MHww&ixlib=rb-4.1.0&q=85)",
          backgroundSize: "cover",
          backgroundPosition: "center",
        }}
      >
        <div className="absolute inset-0 bg-primary/70" />
        <div className="relative h-full flex flex-col justify-between p-12 text-primary-foreground">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-md bg-primary-foreground/15 backdrop-blur flex items-center justify-center border border-primary-foreground/30">
              <Building2 className="w-5 h-5" />
            </div>
            <div>
              <div className="font-display font-bold text-lg leading-none">Param Social Foundation</div>
              <div className="eyebrow mt-1 opacity-80">Hostel ERP</div>
            </div>
          </div>

          <div>
            <div className="eyebrow opacity-70 mb-3">Enterprise ready</div>
            <h1 className="font-display text-5xl xl:text-6xl leading-[0.95] font-black">
              Every bed.<br />Every bill.<br />
              <span className="italic font-light">One system.</span>
            </h1>
            <p className="mt-6 max-w-md text-sm opacity-85 leading-relaxed">
              A cloud-native ERP built for 480+ bed hostels — residents, fees, mess, complaints,
              attendance and an AI copilot, all in one modular platform.
            </p>
          </div>

          <div className="grid grid-cols-3 gap-6 text-primary-foreground/90 text-sm">
            <div>
              <div className="font-display text-3xl font-bold">480+</div>
              <div className="eyebrow opacity-70 mt-1">Beds</div>
            </div>
            <div>
              <div className="font-display text-3xl font-bold">4</div>
              <div className="eyebrow opacity-70 mt-1">Roles</div>
            </div>
            <div>
              <div className="font-display text-3xl font-bold">AI</div>
              <div className="eyebrow opacity-70 mt-1">Copilot</div>
            </div>
          </div>
        </div>
      </div>

      {/* Right form */}
      <div className="flex items-center justify-center p-6 md:p-12 bg-background">
        <div className="w-full max-w-md">
          <div className="eyebrow text-muted-foreground mb-3">Sign in</div>
          <h2 className="font-display text-4xl font-bold leading-tight mb-2">
            Welcome back.
          </h2>
          <p className="text-muted-foreground mb-8 text-sm">
            Use your Param Foundation credentials to access the ERP.
          </p>

          <Card className="p-6 border-border shadow-none">
            <form onSubmit={submit} className="space-y-5">
              <div className="space-y-2">
                <Label htmlFor="email">Email</Label>
                <Input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  data-testid="login-email-input"
                  placeholder="you@example.com"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="password">Password</Label>
                <Input
                  id="password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  data-testid="login-password-input"
                />
              </div>

              {error && (
                <div
                  className="text-sm text-destructive bg-destructive/10 border border-destructive/30 rounded-md px-3 py-2"
                  data-testid="login-error"
                >
                  {error}
                </div>
              )}

              <Button
                type="submit"
                className="w-full h-11"
                disabled={loading}
                data-testid="login-submit-button"
              >
                {loading ? "Signing in..." : "Sign in"}
              </Button>
            </form>
          </Card>

          <div className="mt-6 text-xs text-muted-foreground">
            <span className="eyebrow">Contact your administrator</span>
            <p className="mt-2 leading-relaxed">
              Reach out to your admin to be added as a warden, accountant or resident. Only invited
              users can sign in.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
